
import { computed, defineComponent, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { PropType } from 'vue'
import ConfirmOrder from '@/views/dashboard/service/_components/confirm-order/confirm-order.vue'
import OrderPrice from '@/views/dashboard/service/_components/order-price/order-price.vue'
import { flatAllService, PriceList, ServiceCommonService } from '@/views/dashboard/service/service-common.service'
import { PayType } from '@/views/dashboard/service/_enum/pay-type'
import { useRouter } from 'vue-router'
import type { LocationQuery } from 'vue-router'
import StepList from '@/views/_components/step-list/step-list.vue'
import PayError from '@/views/dashboard/service/_components/pay-error/pay-error.vue'
import PayNative from '@/views/dashboard/service/_components/pay-native/pay-native.vue'
import OfficePay from '@/views/dashboard/service/_components/office-pay/office-pay.vue'
import PayDetails from '@/views/dashboard/service/_components/pay-details/pay-details.vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  GoodsDetails,
} from '@/views/dashboard/service/service-common.service'
import {
  CalcPriceRes, CYCLE_DESC, CycleServiceItem, formatCycleData,
  SubscribeDetailsService
} from '@/views/dashboard/service/subscribe-details/subscribe-details.service'
import { OrderAction } from '@/views/dashboard/service/_enum/order-action'
import { DateKindLabel } from '@/views/dashboard/service/_enum/date-kind'
import ServiceEnum from '@/views/dashboard/service/_components/service-enum/service-enum.vue'
import CmTag from '@/views/dashboard/_components/cm-tag.vue'
import { formatTimeStamp } from '../../../../utils/time'
import SubscribeDetailsTable
  from '@/views/dashboard/service/_components/subscribe-details-table/subscribe-details-table.vue'
import FormulaDetail from '@/views/dashboard/service/_components/formula-detail/formula-detail.vue'
import RenewalService from '@/views/dashboard/service/subscribe-details/_components/renewal-service.vue'
import { ServiceList } from '@/views/dashboard/service/buy-more/buy-more.service'
import { AreaType } from '@/views/dashboard/service/_enum/area-type'
import { HIDE_FUNC } from '@/common/const/hide-function-config'
import { ResponseError } from '@/services/http'
import { errorKeyMap } from '@/common/error/error-code'

const STEP_RENEW = 'renew'
const STEP_OPTIONS = 'options'
const STEP_CONFIRM = 'confirm'
const STEP_PAY = 'pay'
export default defineComponent({
  name: 'BuyVip',
  methods: { formatTimeStamp },
  props: {
    /** 为 true 时表示在侧边抽屉中展示 */
    drawerMode: { type: Boolean, default: false },
    /** 订阅详情初始化参数，统一由父组件传入 */
    initialRouteQuery: { type: Object as PropType<LocationQuery>, default: () => ({}) },
    /** 批量续订（原路由 renew_all），由父组件传入，不再从 initialRouteQuery 解析 */
    renewAll: { type: Boolean, default: false },
  },
  emits: ['close'],
  components: {
    RenewalService,
    FormulaDetail,
    SubscribeDetailsTable,
    CmTag,
    ServiceEnum,
    OfficePay,
    ConfirmOrder,
    StepList,
    PayNative,
    OrderPrice,
    PayError,
    PayDetails,
  },
  setup (props, { emit }) {
    const { t, locale } = useI18n()
    const injector = useReflectiveInjector([ServiceCommonService, SubscribeDetailsService])
    const serviceCommonService = injector.get(ServiceCommonService)
    const service = injector.get(SubscribeDetailsService)

    const router = useRouter()
    const stepList = ref<string[]>([STEP_OPTIONS, STEP_CONFIRM, STEP_PAY])
    const stepDisplayList = computed(() => {
      void locale.value
      return stepList.value.map(id => t(`subscribeDetails.steps.${id}`))
    })

    function areaTypeLabel (areaType: number | string): string {
      if (+areaType === AreaType.linux) {
        return t('subscribeDetails.areaTypeLinux')
      }
      return HIDE_FUNC.macos
        ? t('subscribeDetails.areaTypeNormalNoMac')
        : t('subscribeDetails.areaTypeNormal')
    }
    const effectiveQuery = computed<LocationQuery>(() => props.initialRouteQuery || {})
    const currentStepIndex = ref(0)
    const confirmOrderNode = ref()
    const dialogErrorPayVisible = ref<boolean>(false)
    const dialogOrderVisible = ref<boolean>(false)
    const cycleCount = ref(1)
    const areaList = ref(
      effectiveQuery.value.area_list
        ? (effectiveQuery.value.area_list as string)?.split(',')
        : [AreaType.normal]
    )
    // currentArea为空时代表同时展示所有专区
    const currentArea = ref(undefined)
    const serviceMergeListCycle = ref<CycleServiceItem[]>([])
    const serviceListSelected = ref<ServiceList[]>([])
    const queryParams = reactive({
      price_ids: [],
      action: OrderAction.open,
      purchase_count: 0,
      is_cancel: 0,
      pay_type: 1,
      old_product_id: effectiveQuery.value.old_product_id as string || undefined,
    })
    const companyName = ref('')

    const payParams = reactive({
      product_id: ''
    })

    const viewModel = reactive({
      priceData: {} as CalcPriceRes,
      detailData: {} as GoodsDetails,
      detailOrderStatus: '',
    })

    const serviceMergeList = computed(() => {
      const currentCyclePrices = serviceMergeListCycle.value?.find(v => v.cycle_count === cycleCount.value)?.price_list || []
      return currentCyclePrices.filter(v => v.area_type === (+currentArea.value || v.area_type))
    })

    const orderInfo = computed<PriceList>(() => {
      return serviceMergeList.value[0] || {} as PriceList
    })

    function updateCalcPrice () {
      if (!queryParams.purchase_count) {
        queryParams.purchase_count = 1
      }

      service.calcPrice(queryParams).then(response => {
        viewModel.priceData = response
      })
    }

    function payCurrent () {
      queryParams.is_cancel = 1
      service.createOrder(queryParams).then((res) => {
        payParams.product_id = res.product_id
        dialogErrorPayVisible.value = false
        confirmNext()
      }).catch((error: ResponseError) => {
        const ret = Number((error as any)?.responseBody?.ret)
        const msg = String((error as any)?.responseBody?.msg || (error as any)?.message || '')
        if (ret === errorKeyMap.HAD_PAYING_ORDER.ret || msg.includes('未支付订单')) {
          dialogErrorPayVisible.value = true
        }
      })
    }

    function handleConfirmOrder (data) {
      queryParams.pay_type = data.payType

      queryParams.is_cancel = 0

      service.createOrder(queryParams).then((res) => {
        if (res.err_code === 0) {
          payParams.product_id = res.product_id
          confirmNext()
        } else {
          dialogErrorPayVisible.value = true
          payParams.product_id = res.product_id
        }
      }).catch((error: ResponseError) => {
        const ret = Number((error as any)?.responseBody?.ret)
        const msg = String((error as any)?.responseBody?.msg || (error as any)?.message || '')
        if (ret === errorKeyMap.HAD_PAYING_ORDER.ret || msg.includes('未支付订单')) {
          dialogErrorPayVisible.value = true
        }
      })
    }

    function toHistory () {
      if (payParams.product_id) {
        dialogErrorPayVisible.value = false
        dialogOrderVisible.value = true
        return
      }
      router.push(`/dashboard/order-history`)
      if (props.drawerMode) emit('close')
    }

    function handleDeferRenew () {
      emit('close')
    }

    function handleOfficePayConfirm () {
      if (props.drawerMode) {
        emit('close')
      }
    }

    function handleNavigateServiceSubscribe () {
      router.push('/dashboard/service-subscribe')
      if (props.drawerMode) emit('close')
    }

    async function handleNextStep () {
      if (stepList.value[currentStepIndex.value] === STEP_CONFIRM) {
        confirmOrderNode.value.checkConfirm()
      } else {
        confirmNext()
      }
    }

    function confirmNext () {
      currentStepIndex.value++
    }

    async function changeRenewalServiceSelect (price_ids: number[], list) {
      serviceListSelected.value = list
      if (!price_ids.length) {
        return
      }

      const data = await serviceCommonService.getAllService({ price_ids: price_ids })
      companyName.value = data.company_name
      serviceMergeListCycle.value = formatCycleData(flatAllService(data.prices))
    }

    function updatePriceIds () {
      queryParams.price_ids = serviceMergeList.value.map(v => v.price_id)
      updateCalcPrice()
    }

    /** 来源-服务选择页面 */
    async function initDataFromChoose () {
      const routeQuery = effectiveQuery.value
      const serviceUnionIdsStr = routeQuery.service_union_ids as string
      const groupUnionIdsStr = routeQuery.group_union_ids as string
      companyName.value = routeQuery.company_name as string
      const serviceUnionIds = serviceUnionIdsStr ? serviceUnionIdsStr?.split(',').map(v => +v) : []
      const groupUnionIds = groupUnionIdsStr ? groupUnionIdsStr?.split(',').map(v => +v) : []
      serviceMergeListCycle.value = await service.getCycleServiceMergeByUnionIds(groupUnionIds, serviceUnionIds)
    }

    /** 来源-订单续购 */
    async function initDataFromRenew () {
      const routeQuery = effectiveQuery.value
      const data = await service.getSubscribeDetails({
        product_id: routeQuery.old_product_id as string,
      })
      queryParams.purchase_count = data.purchase_count || 1
      cycleCount.value = +(routeQuery.cycle_count || 1)
      companyName.value = data.company_name
      queryParams.action = OrderAction.open
      serviceMergeListCycle.value = formatCycleData(data.price_list)
    }

    /** 来源-price_id（权限功能页 我的订阅页面） */
    async function initDataFromPermission () {
      const routeQuery = effectiveQuery.value
      const normal_price_ids = routeQuery.normal_price_id ? [+routeQuery.normal_price_id] : []
      const linux_price_ids = routeQuery.linux_price_id ? [+routeQuery.linux_price_id] : []
      const serviceData = await serviceCommonService.getAllService({ price_ids: normal_price_ids })
      const linuxGroup = await serviceCommonService.getServiceGroup({ price_ids: linux_price_ids, area_type: 2 })
      const allPrices = [...flatAllService(serviceData.prices), ...linuxGroup.price_list]
      currentArea.value = areaList.value[0]
      companyName.value = serviceData.company_name
      queryParams.purchase_count = +(routeQuery.purchase_count || 1)
      serviceMergeListCycle.value = formatCycleData(allPrices)
    }

    /** 来源-服务批量续订 */
    async function initDataFromBatchRenew () {
      stepList.value.unshift(STEP_RENEW)
    }

    function initData () {
      const routeQuery = effectiveQuery.value
      areaList.value = routeQuery.area_list
        ? (routeQuery.area_list as string)?.split(',')
        : [AreaType.normal]
      queryParams.old_product_id = routeQuery.old_product_id as string || undefined
      if (routeQuery.service_union_ids || routeQuery.group_union_ids) {
        initDataFromChoose()
      } else if (routeQuery.old_product_id) {
        initDataFromRenew()
      } else if (routeQuery.normal_price_id || routeQuery.linux_price_id) {
        initDataFromPermission()
      } else if (props.renewAll) {
        initDataFromBatchRenew()
      }
    }

    watch(
      () => [props.initialRouteQuery, props.renewAll] as const,
      () => {
        initData()
      },
      { immediate: true, deep: true }
    )

    watch(serviceMergeList, (value) => {
      if (value.length) {
        nextTick(updatePriceIds)
      }
    })

    return {
      CYCLE_DESC,
      PayType,
      AreaType,
      STEP_RENEW,
      STEP_OPTIONS,
      STEP_CONFIRM,
      STEP_PAY,
      stepDisplayList,
      areaTypeLabel,
      drawerMode: props.drawerMode,
      stepList,
      orderInfo,
      companyName,
      areaList,
      currentArea,
      serviceMergeListCycle,
      serviceMergeList,
      cycleCount,
      confirmOrderNode,
      currentStepIndex,
      serviceListSelected,
      queryParams,
      payParams,
      viewModel,
      dialogErrorPayVisible,
      dialogOrderVisible,
      toHistory,
      payCurrent,
      changeRenewalServiceSelect,
      updatePriceIds,
      DateKindLabel,
      handleConfirmOrder,
      handleNextStep,
      updateCalcPrice,
      handleDeferRenew,
      handleNavigateServiceSubscribe,
      handleOfficePayConfirm,
    }
  }
})
